import { createSlice } from '@reduxjs/toolkit'

import {
  getAllNews,
  getPaginatedNews,
  // getAllNewsWithKeyword,
  createNews,
  likeNews,
  reshoutNews,
  getMediumPosts,
  commentNews,
  getNewsDetails,
  updateNews,
  deleteNews,
  createNewsForGame,
  getNewsByGameId,
  deleteComment,
  createQuoteShout,
} from './actions'
import { State } from './types'
import { handleUploadError } from 'utils/uploadErrorHandler'

const initialState: State = {
  isLoading: false,
  deleteIsLoading: false,
  isPostingNews: false,
  isCommentingNews: false,
  isMediumLoading: false,
  isInteracting: false,
  postNews: null,
  news: [],
  // gameNews: {},
  gameNews: [],
  messageForCreate: '',
  newsDetails: {},
  mediumPosts: [],
  error: null,
  restrictionCode: null,
  restrictedFor: null,
  commentMessage: '',
  commentData: {},
  createCommentLoader: false,
  // Pagination state
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
    hasNext: false,
    hasPrev: false
  }
}

const newsSlice = createSlice({
  name: 'news',
  initialState,
  reducers: {
    messageForCreateCleanUp(state) {
      state.messageForCreate = ''
    },
    gameNewsCleanUp(state) {
      state.gameNews = []
    },
    commentMessageCleanUp(state) {
      state.commentMessage = ''
    },
    newsDetailsCleanUp(state) {
      state.newsDetails = {}
      state.restrictionCode = null
      state.restrictedFor = null
    },
    updatePostBlockchainMetadata(state, action) {
      const { postId, blockchainMetadata } = action.payload
      // Add validated status to the metadata
      const validatedMetadata = {
        ...blockchainMetadata,
        status: 'validated'
      }
      // Update in main news array
      state.news = state.news.map((item: any) => {
        if (item.news._id === postId || item.news.id === postId) {
          return {
            ...item,
            news: {
              ...item.news,
              blockchainMetadata: validatedMetadata
            }
          }
        }
        return item
      })
      // Update in news details if present
      if (state.newsDetails[postId]) {
        state.newsDetails[postId].news.blockchainMetadata = validatedMetadata
      }
    },
    updateCommentBlockchainMetadata(state, action) {
      const { commentId, newsId, blockchainMetadata } = action.payload
      // Add validated status to the metadata
      const validatedMetadata = {
        ...blockchainMetadata,
        status: 'validated'
      }
      // Update in news details comments
      if (state.newsDetails[newsId]) {
        state.newsDetails[newsId].comments = state.newsDetails[newsId].comments.map((comment: any) => {
          if (comment.commentBody._id === commentId || comment.commentBody.id === commentId) {
            return {
              ...comment,
              commentBody: {
                ...comment.commentBody,
                blockchainMetadata: validatedMetadata
              }
            }
          }
          return comment
        })
      }
      // Update in main news array comments if they exist
      state.news = state.news.map((item: any) => {
        if (item.news._id === newsId || item.news.id === newsId) {
          if (item.comments) {
            item.comments = item.comments.map((comment: any) => {
              if (comment.commentBody._id === commentId || comment.commentBody.id === commentId) {
                return {
                  ...comment,
                  commentBody: {
                    ...comment.commentBody,
                    blockchainMetadata: validatedMetadata
                  }
                }
              }
              return comment
            })
          }
        }
        return item
      })
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllNews.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getAllNews.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.news = payload
      })
      .addCase(getAllNews.rejected, (state, action) => {
        state.isLoading = false
        if (action.payload) {
          state.error = action.payload.errors
        } else {
          state.error = action.error.message
        }
      })
      // Paginated news cases
      .addCase(getPaginatedNews.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getPaginatedNews.fulfilled, (state, { payload }) => {
        state.isLoading = false
        // Append new news to existing ones for infinite scroll
        if (payload && payload.data && Array.isArray(payload.data)) {
          const currentPage = payload.pagination?.currentPage || 1

          if (currentPage === 1) {
            // First page - replace all data (fresh load)
            state.news = payload.data
          } else {
            // Subsequent pages - append with deduplication
            const existingIds = new Set(state.news.map((item: any) => item.news._id))
            const newItems = payload.data.filter((item: any) => !existingIds.has(item.news._id))
            state.news = [...state.news, ...newItems]
          }
          state.pagination = payload.pagination
        } else {
          console.error('Invalid payload structure in getPaginatedNews:', payload)
        }
      })
      .addCase(getPaginatedNews.rejected, (state, action) => {
        state.isLoading = false
        if (action.payload) {
          state.error = action.payload.errors
        } else {
          state.error = action.error.message
        }
      })
      .addCase(getNewsDetails.pending, (state) => {
        state.isLoading = true
        state.error = null
        state.restrictionCode = null
        state.restrictedFor = null
      })
      .addCase(
        getNewsDetails.fulfilled,
        (state, { meta: { arg }, payload }) => {
          const { newsId } = arg
          state.isLoading = false
          state.restrictionCode = null
          state.restrictedFor = null
          state.newsDetails = { ...state.newsDetails, [newsId]: payload }
        },
      )
      .addCase(getNewsDetails.rejected, (state, action) => {
        state.isLoading = false
        // Capture the moderation-privacy code (e.g. SHOUT_RESTRICTED) + the newsId it applies
        // to so the view can render a "log in to view" gate without flashing it on the next
        // shout during in-place navigation.
        state.restrictionCode = (action.payload as any)?.code ?? null
        state.restrictedFor = (action.meta?.arg as any)?.newsId ?? null
        if (action.payload) {
          state.error = action.payload.errors
        } else {
          state.error = action.error.message
        }
      })
      .addCase(createNews.pending, (state) => {
        state.isPostingNews = true
        state.error = null
      })
      .addCase(createNews.fulfilled, (state, { payload }) => {
        state.isPostingNews = false
        state.postNews = payload
        // Add pending blockchain metadata for the new post
        const newsWithPending = {
          ...payload.news,
          blockchainMetadata: {
            status: 'pending',
            validatedAt: null,
            transactionId: null,
            blockHash: null,
            ordinal: null,
            stateHash: null
          }
        }
        // Add new post to the beginning of the feed with empty comments array
        state.news = [
          {
            news: newsWithPending,
            creator: payload.creator,
            comments: [], // Initialize empty comments array
          },
          ...state.news,
        ]
      })
      .addCase(createNews.rejected, (state, action) => {
        state.isPostingNews = false
        state.createCommentLoader = false
        if (action.payload) {
          state.error = action.payload.errors || action.payload.message
          // Show upload error toast notification
          handleUploadError(
            { response: { data: { message: action.payload.errors || action.payload.message } } },
            'Failed to create post'
          )
        } else {
          state.error = action.error.message
          handleUploadError({ message: action.error.message }, 'Failed to create post')
        }
      })
      .addCase(commentNews.pending, (state) => {
        state.createCommentLoader = true
        state.isCommentingNews = true
        state.error = null
      })
      .addCase(commentNews.fulfilled, (state, action) => {
        state.isCommentingNews = false
        state.createCommentLoader = false

        // Add pending blockchain metadata for the new comment
        const commentWithPending = {
          ...action.payload.comment.commentBody,
          blockchainMetadata: {
            status: 'pending',
            validatedAt: null,
            transactionId: null,
            blockHash: null,
            ordinal: null,
            stateHash: null
          }
        }

        state.commentData = action.payload.comment
        state.commentMessage = action.payload.message
        state.news = state.news.map((newsItem: any) => {
          if (
            newsItem.news._id === action.payload.comment.commentBody.news_id
          ) {
            // Ensure comments array exists before spreading
            const existingComments = newsItem.comments || []
            return {
              ...newsItem,
              comments: [
                {
                  whoCommentThis: action.payload.comment.whoCommentThis,
                  commentBody: commentWithPending,
                },
                ...existingComments,
              ]
            }
          }
          return newsItem
        })

        state.gameNews = state.gameNews.map((newsItem: any) => {
          if (
            newsItem.news._id === action.payload.comment.commentBody.news_id
          ) {
            // Ensure comments array exists before spreading
            const existingComments = newsItem.comments || []
            return {
              ...newsItem,
              comments: [
                {
                  whoCommentThis: action.payload.comment.whoCommentThis,
                  commentBody: action.payload.comment.commentBody,
                },
                ...existingComments,
              ]
            }
          }
          return newsItem
        })

        console.log('action.payload', action.payload)

        // The backend returns comment: { whoCommentThis: {...}, commentBody: {...} }
        const newComment = {
          whoCommentThis: action.payload.comment.whoCommentThis,
          commentBody: action.payload.comment.commentBody
        };

        state.newsDetails[action.payload.comment.commentBody.news_id] = {
          ...state.newsDetails[action.payload.comment.commentBody.news_id],
          comments: [
            newComment,
            ...(state.newsDetails[action.payload.comment.commentBody.news_id]
              ?.comments || []),
          ],
        }

        // state.newsDetails = {
        //   ...state.newsDetails,
        //   [action.payload.comment.commentBody.news_id]: {
        //     ...state.newsDetails[action.payload.comment.commentBody.news_id],
        //     comments: [
        //       {
        //         whoCommentThis: action.payload.comment.whoCommentThis,
        //         commentBody: action.payload.comment.commentBody,
        //       },
        //       ...(state.newsDetails[action.payload.comment.commentBody.news_id]
        //         ?.comments || []),
        //     ],
        //   },
        // }
      })

      .addCase(commentNews.rejected, (state, action: any) => {
        state.isCommentingNews = false
        state.createCommentLoader = false
        if (action.payload) {
          state.error = action.payload.errors || action.payload.message
          // Show upload error toast notification
          handleUploadError(
            { response: { data: { message: action.payload.errors || action.payload.message } } },
            'Failed to post comment'
          )
        } else {
          state.error = action.error.message
          handleUploadError({ message: action.error.message }, 'Failed to post comment')
        }
      })
      .addCase(likeNews.pending, (state) => {
        state.isInteracting = true
        state.error = null
      })
      .addCase(likeNews.fulfilled, (state, { meta }) => {
        const {
          arg: { userId, newsId },
        } = meta
        state.isInteracting = false

        const modifiedNews = state.news.map((newsItem) => {
          if (newsItem.news._id === newsId) {
            const likesWithoutMe = newsItem.news.like.filter(
              (item) => item !== userId,
            )

            return {
              ...newsItem,
              news: {
                ...newsItem.news,
                like: newsItem.news.like.includes(userId)
                  ? likesWithoutMe
                  : [...newsItem.news.like, userId],
              },
            }
          }

          return newsItem
        })

        state.news = modifiedNews
      })
      .addCase(likeNews.rejected, (state, action) => {
        state.isInteracting = false
        if (action.payload) {
          state.error = action.payload.errors || action.payload.message
        } else {
          state.error = action.error.message
        }
      })
      .addCase(reshoutNews.pending, (state) => {
        state.isInteracting = true
        state.error = null
      })
      .addCase(reshoutNews.fulfilled, (state, { payload, meta }) => {
        const {
          arg: { userId, newsId },
        } = meta
        state.isInteracting = false

        // Check if this was an un-reshout (removing boost)
        const wasAlreadyReshoutedByUser = state.news.some(
          (item) => item.news._id === newsId && item.news.retweet?.includes(userId)
        )

        // Update retweet count on the original post
        const modifiedReshouts = state.news.map((newsItem) => {
          if (newsItem.news._id === newsId) {
            const reshoutsWithoutMe = newsItem.news.retweet.filter(
              (item) => item !== userId,
            )

            return {
              ...newsItem,
              news: {
                ...newsItem.news,
                retweet: newsItem.news.retweet.includes(userId)
                  ? reshoutsWithoutMe
                  : [...newsItem.news.retweet, userId],
              },
            }
          }

          return newsItem
        })

        // If this was a new reshout (not un-reshout), add boosted entry to top of feed
        if (!wasAlreadyReshoutedByUser && payload?.feedItem) {
          // Add the boosted post at the top of the feed
          state.news = [
            {
              news: payload.feedItem.news,
              creator: payload.feedItem.creator,
              comments: [],
              boostedBy: payload.feedItem.boostedBy,
              boostedAt: payload.feedItem.boostedAt,
              isBoosted: true,
            },
            ...modifiedReshouts,
          ]
        } else {
          state.news = modifiedReshouts
        }
      })
      .addCase(reshoutNews.rejected, (state, action) => {
        state.isInteracting = false
        if (action.payload) {
          state.error = action.payload.errors || action.payload.message
        } else {
          state.error = action.error.message
        }
      })
      // .addCase(getAllNewsWithKeyword.pending, (state) => {
      //   state.isLoading = true
      //   state.error = null
      // })
      // .addCase(getAllNewsWithKeyword.fulfilled, (state, { meta, payload }) => {
      //   state.isLoading = false
      //   state.gameNews = {
      //     ...state.gameNews,
      //     [meta.arg]: payload.reverse(),
      //   }
      // })
      // .addCase(getAllNewsWithKeyword.rejected, (state) => {
      //   state.isLoading = false
      // })
      .addCase(getMediumPosts.pending, (state) => {
        state.isMediumLoading = true
        state.error = null
      })
      .addCase(getMediumPosts.fulfilled, (state, { payload }) => {
        state.isMediumLoading = false
        state.mediumPosts = payload ?? []
      })
      .addCase(getMediumPosts.rejected, (state) => {
        state.isMediumLoading = false
      })
      .addCase(updateNews.pending, (state) => {
        state.isPostingNews = true
        state.error = null
      })
      .addCase(updateNews.fulfilled, (state, { payload }) => {
        state.isPostingNews = false
        state.postNews = payload
        state.commentMessage === 'News updated successfully'
        // console.log('payload', payload)
        state.news = state.news.map((newsItem) => {
          if (newsItem.news._id === payload.updateNewsData._id) {
            return {
              news: payload.updateNewsData,
              creator: payload.creator,
            }
          }
          return newsItem
        })
      })
      .addCase(updateNews.rejected, (state, action) => {
        state.isPostingNews = false
        if (action.payload) {
          state.error = action.payload.errors || action.payload.message
          // Show upload error toast notification
          handleUploadError(
            { response: { data: { message: action.payload.errors || action.payload.message } } },
            'Failed to update post'
          )
        } else {
          state.error = action.error.message
          handleUploadError({ message: action.error.message }, 'Failed to update post')
        }
      })
      .addCase(deleteNews.pending, (state) => {
        // state.isLoading = true
        state.deleteIsLoading = true
        state.error = null
      })
      .addCase(deleteNews.fulfilled, (state, { meta }) => {
        console.log('meta', meta)

        const newsId = meta.arg
        // console.log('newsId', newsId)
        state.commentMessage = 'News deleted successfully'
        // remove news from list of news
        state.news = state.news.filter(
          (newsItem) => newsItem.news._id !== newsId,
        )
        state.gameNews = state.gameNews.filter(
          (newsItem) => newsItem.news._id !== newsId,
        )

        state.deleteIsLoading = false
      })
      .addCase(deleteNews.rejected, (state, action) => {
        // state.isLoading = false
        state.deleteIsLoading = false
        if (action.payload) {
          state.error = action.payload.errors || action.payload.message
        } else {
          state.error = action.error.message
        }
      })
      .addCase(createNewsForGame.pending, (state) => {
        state.isPostingNews = true
        state.error = null
      })
      .addCase(createNewsForGame.fulfilled, (state, { payload }) => {
        state.isPostingNews = false
        state.postNews = payload
        state.messageForCreate = 'News created successfully'

        state.news = [
          {
            news: payload.news,
            creator: payload.creator,
            comments: [], // Initialize empty comments array
          },
          ...state.news,
        ]
        state.gameNews = [payload, ...state.gameNews]
      })
      .addCase(createNewsForGame.rejected, (state, action) => {
        state.isPostingNews = false
        if (action.payload) {
          state.error = action.payload.errors || action.payload.message
          // Show upload error toast notification
          handleUploadError(
            { response: { data: { message: action.payload.errors || action.payload.message } } },
            'Failed to create game post'
          )
        } else {
          state.error = action.error.message
          handleUploadError({ message: action.error.message }, 'Failed to create game post')
        }
      })
      .addCase(getNewsByGameId.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getNewsByGameId.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.gameNews = payload
      })
      .addCase(getNewsByGameId.rejected, (state, action) => {
        state.isLoading = false
        if (action.payload) {
          state.error = action.payload.errors
        } else {
          state.error = action.error.message
        }
      })
      .addCase(deleteComment.pending, (state) => {
        state.createCommentLoader = true
        state.error = null
      })
      .addCase(deleteComment.fulfilled, (state, { meta }) => {
        console.log('meta', meta)

        const { commentId, newsId } = meta.arg

        state.createCommentLoader = false

        state.newsDetails[newsId] = {
          ...state.newsDetails[newsId],
          comments: state.newsDetails[newsId].comments.filter(
            (comment: any) => comment.commentBody._id != commentId,
          ),
        }
        state.commentMessage = 'Comment deleted successfully'
      })

      .addCase(deleteComment.rejected, (state, action) => {
        state.createCommentLoader = false
        if (action.payload) {
          state.error = action.payload.errors || action.payload.message
        } else {
          state.error = action.error.message
        }
      })
      // Quote Shout handlers
      .addCase(createQuoteShout.pending, (state) => {
        state.isPostingNews = true
        state.error = null
      })
      .addCase(createQuoteShout.fulfilled, (state, { payload }) => {
        state.isPostingNews = false
        state.postNews = payload

        // Get the original post update info from backend
        const originalPostUpdate = payload.originalPostUpdate

        // Update the original post's retweet array with the new data from backend
        const updatedNews = state.news.map((newsItem) => {
          if (originalPostUpdate && newsItem.news._id === originalPostUpdate.postId) {
            return {
              ...newsItem,
              news: {
                ...newsItem.news,
                retweet: originalPostUpdate.retweet,
              },
            }
          }
          return newsItem
        })

        // Add the new quote shout to the beginning of the feed
        state.news = [
          {
            news: {
              ...payload.news,
              blockchainMetadata: {
                status: 'pending',
                validatedAt: null,
                transactionId: null,
                blockHash: null,
                ordinal: null,
                stateHash: null
              }
            },
            creator: payload.creator,
            comments: [],
          },
          ...updatedNews,
        ]
      })
      .addCase(createQuoteShout.rejected, (state, action) => {
        state.isPostingNews = false
        if (action.payload) {
          state.error = action.payload.errors || action.payload.message
          handleUploadError(
            { response: { data: { message: action.payload.errors || action.payload.message } } },
            'Failed to create quote shout'
          )
        } else {
          state.error = action.error.message
          handleUploadError({ message: action.error.message }, 'Failed to create quote shout')
        }
      })
  },
})

export const { reducer, actions } = newsSlice

export const {
  messageForCreateCleanUp,
  gameNewsCleanUp,
  commentMessageCleanUp,
  newsDetailsCleanUp,
  updatePostBlockchainMetadata,
  updateCommentBlockchainMetadata,
} = newsSlice.actions

export default newsSlice
